import {
  cardClassName,
  chartColors,
  panelSubtleClassName,
  panelTitleClassName,
} from "@/modules/utils/dashboard/constants";
import { FunnelPoint, PipelinePoint } from "@/modules/types/dashboard";
import {
  dashboardTooltipStyle,
  formatDashboardNumber,
} from "@/modules/utils/dashboard/formatter";
import { useClientTranslations } from "@/lib/translate";
import {
  Cell,
  Funnel,
  FunnelChart,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

type DashboardMainAnalyticsSectionProps = {
  conversionRate: number;
  funnelData: FunnelPoint[];
  pipelineData: PipelinePoint[];
  activeCandidates: number;
};

export const DashboardMainAnalyticsSection = ({
  conversionRate,
  funnelData,
  pipelineData,
  activeCandidates,
}: DashboardMainAnalyticsSectionProps) => {
  const { t } = useClientTranslations("dashboard-page");
  console.log(funnelData);

  const localizedFunnelData = funnelData.map((item) => ({
    ...item,
    label:
      item.stage === "applied"
        ? t("dashboard.mainAnalytics.appliedWithPercent", {
            percent: conversionRate.toFixed(1),
          })
        : t(`dashboard.mainAnalytics.${item.stage}`),
  }));

  const localizedPipelineData = pipelineData.map((item) => ({
    ...item,
    name: t(`dashboard.mainAnalytics.pipeline.${item.name}`),
  }));

  return (
    <section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <article className={`${cardClassName} xl:col-span-8`}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className={panelTitleClassName}>
            {t("dashboard.mainAnalytics.attractionFunnelTitle")}
          </h2>
          <p className={panelSubtleClassName}>
            {t("dashboard.mainAnalytics.conversion")}:{" "}
            {conversionRate.toFixed(1)}%
          </p>
        </div>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <FunnelChart>
              <defs>
                <linearGradient id="funnelGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={chartColors.primary} />
                  <stop offset="100%" stopColor={chartColors.secondary} />
                </linearGradient>
              </defs>
              <Tooltip
                formatter={(value) => {
                  const numericValue = Array.isArray(value)
                    ? Number(value[0] ?? 0)
                    : Number(value ?? 0);
                  return formatDashboardNumber(
                    Number.isFinite(numericValue) ? numericValue : 0,
                  );
                }}
                contentStyle={dashboardTooltipStyle}
              />
              <Funnel
                dataKey="value"
                data={localizedFunnelData}
                isAnimationActive
              >
                <LabelList
                  position="center"
                  stroke="none"
                  fill="#ffffff"
                  dataKey="label"
                />
                {localizedFunnelData.map((item) => (
                  <Cell key={item.stage} fill="url(#funnelGradient)" />
                ))}
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </div>
      </article>

      <article className={`${cardClassName} xl:col-span-4`}>
        <h2 className={`mb-3 ${panelTitleClassName}`}>
          {t("dashboard.mainAnalytics.candidatePipelineTitle")}
        </h2>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip
                formatter={(value) => {
                  const numericValue = Array.isArray(value)
                    ? Number(value[0] ?? 0)
                    : Number(value ?? 0);
                  return formatDashboardNumber(
                    Number.isFinite(numericValue) ? numericValue : 0,
                  );
                }}
                contentStyle={dashboardTooltipStyle}
              />
              <Legend verticalAlign="bottom" height={40} />
              <Pie
                data={localizedPipelineData}
                dataKey="value"
                innerRadius={58}
                outerRadius={90}
                paddingAngle={2}
                strokeWidth={0}
              >
                <LabelList
                  dataKey="name"
                  position="outside"
                  formatter={(value) => String(value ?? "")}
                  fill="var(--foreground)"
                />
                {localizedPipelineData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <text
                x="50%"
                y="46%"
                textAnchor="middle"
                dominantBaseline="middle"
                fill="var(--foreground)"
                className="text-xs opacity-70"
              >
                {t("dashboard.mainAnalytics.active")}
              </text>
              <text
                x="50%"
                y="54%"
                textAnchor="middle"
                dominantBaseline="middle"
                fill="var(--foreground)"
                className="text-sm font-semibold"
              >
                {formatDashboardNumber(activeCandidates)}
              </text>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </article>
    </section>
  );
};
